import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import { GetApp } from "@material-ui/icons";
import { CSVLink } from "react-csv";
import * as C from "components";

const useStyles = makeStyles({
  loaderBox: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  loaderBarImg: {
    width: 40,
  },
  tableContainer: {
    display: "flex",
    minHeight: 200,
  },
  dateTableWrapper: {
    float: "left",
  },
  dataTableWrapper: {
    float: "left",
    overflow: "scroll",
  },
  leftTable: {
    border: "1px solid #eee",
  },
  lblGameName: {
    color: "#000",
    fontSize: 13,
    minWidth: 200,
  },
  _GAIv: {
    height: 40,
    backgroundColor: "#f9f9f9",
    borderLeft: "1px solid #eee6e6",
    color: "#666",
    fontSize: 11,
    minWidth: 80,
    paddingRight: 10,
    textAlign: "right",
  },
  dataHeaderTd: {
    width: 100,
    paddingRight: 10,
    borderBottom: "1px solid #eee6e6",
  },
  dataTableBody: {
    fontSize: 11,
    paddingRight: 10,
    paddingBottom: 0,
    textAlign: "right",
    verticalAlign: "middle",
    border: "1px solid #eee6e6",
    height: "3em",
    overflowX: "auto",
  },
  dataCellTd: {
    fontSize: 13,
    paddingRight: 10,
    paddingBottom: 0,
    paddingLeft: 10,
    textAlign: "right",
    verticalAlign: "middle",
    borderBottom: "1px solid #eee6e6",
    borderRight: "1px solid #eee6e6",
    height: 45,
  },
  tTdHeader: {
    paddingLeft: "1rem",
    paddingRight: 19,
    fontWeight: "bold",
    height: 39,
  },
  tTd: {
    height: 44,
    width: 250,
    paddingLeft: "1rem",
    borderBottom: "1px solid #eee",
    borderCollapse: "collapse",
  },
  exportBtn: {
    marginLeft: 16,
  },
  titleContainer: {
    display: "flex",
    flexWrap: "wrap",
  },
  widgetTitle: {
    flexBasis: "50%",
    fontSize: 16,
    padding: 16,
  },
  exportBtnContainer: {
    marginLeft: "auto",
    padding: 16,
  },
});

const formatDateLabel = (value) => {
  const m = moment.utc(value).tz("Asia/Tokyo");
  return m.isValid() ? m.format("M/D") : String(value || "");
};

const RegularSeasonEventTable = ({ rows = [], loading = false }) => {
  const classes = useStyles();

  if (!loading && (!Array.isArray(rows) || rows.length === 0)) {
    return (
      <Paper>
        <div className={classes.titleContainer}>
          <div className={classes.widgetTitle}>撮影実績一覧</div>
        </div>
        <div className={classes.loaderBox}>
          <C.NoData text="No Data" />
        </div>
      </Paper>
    );
  }

  const dates = rows.map((row) => row.day_jst || row.date || "");
  const exportData = (() => {
    if (!rows.length) {
      return [["\uFEFF撮影実績一覧"]];
    }

    const header = ["撮影実績一覧", ...dates.map((date) => formatDateLabel(date))];
    const rowDefs = [
      { label: "撮影された数", key: "unique_shot_count" },
      { label: "レビューOK数", key: "review_ok_count" },
      { label: "ユニーク撮影者数", key: "unique_shooter_count" },
    ];

    const output = [header];
    rowDefs.forEach((def) => {
      output.push([def.label, ...rows.map((row) => Number(row[def.key] || 0))]);
    });
    output[0][0] = `\uFEFF${output[0][0]}`;
    return output;
  })();

  const rowDefs = [
    { label: "撮影された数", key: "unique_shot_count" },
    { label: "レビューOK数", key: "review_ok_count" },
    { label: "ユニーク撮影者数", key: "unique_shooter_count" },
  ];

  const getLabelColumn = () => (
    <>
      {rowDefs.map((row) => (
        <tr key={row.label}>
          <td className={classes.tTd}>
            <div className={classes.lblGameName}>{row.label}</div>
          </td>
        </tr>
      ))}
    </>
  );

  const getDataTable = () => (
    <>
      {rowDefs.map((row) => (
        <tr key={row.label}>
          {rows.map((d, i) => (
            <td className={classes.dataCellTd} key={i}>
              {Number(d[row.key] || 0)}
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <Paper>
      <div className={classes.titleContainer}>
        <div className={classes.widgetTitle}>撮影実績一覧</div>
        <div className={classes.exportBtnContainer}>
          <CSVLink
            data={exportData}
            filename="PicTree - レギュラーシーズンイベント日別撮影実績"
          >
            <Button
              variant="contained"
              color="default"
              className={classes.exportBtn}
              startIcon={<GetApp />}
            >
              CSV出力
            </Button>
          </CSVLink>
        </div>
      </div>
      <div className={classes.tableContainer}>
        {loading ? (
          <div className={classes.loaderBox}>
            <img className={classes.loaderBarImg} src="/static/images/loader-bar.gif" />
          </div>
        ) : (
          <>
            <div className={classes.dateTableWrapper}>
              <table className={classes.leftTable}>
                <tbody>
                  <tr>
                    <td className={classes.tTdHeader} />
                  </tr>
                  {getLabelColumn()}
                </tbody>
              </table>
            </div>

            <div className={classes.dataTableWrapper}>
              <table className={classes.dataTableBody}>
                <tbody>
                  <tr className={classes._GAIv}>
                    {dates.map((date, i) => (
                      <td className={classes.dataHeaderTd} key={i}>
                        {formatDateLabel(date)}
                      </td>
                    ))}
                  </tr>
                  {getDataTable()}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Paper>
  );
};

RegularSeasonEventTable.propTypes = {
  rows: PropTypes.array,
  loading: PropTypes.bool,
};

export default RegularSeasonEventTable;
